import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { API_BASE } from '../config/api'

interface LinkedProject {
  id: string
  title: string
  category?: string
  description: string
  technologies?: string[]
  features?: string[]
  duration?: string
  team?: string
  status?: string
}

interface LinkedReview {
  id: string
  authorName: string
  authorRole: string
  authorInitials: string
  rating: number;
  quote: string;
}

interface CaseStudyDetails {
  id: string
  client: string
  description: string
  resultValue: string
  resultLabel: string
  project?: LinkedProject | null
  review?: LinkedReview | null
  createdAt?: string
}

export default function CaseStudyDetail() {
  const { id } = useParams<{ id: string }>()
  const [caseStudy, setCaseStudy] = useState<CaseStudyDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true
    const fetchCaseStudy = async () => {
      if (!id) {
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)
      try {
        const res = await fetch(`${API_BASE}/api/v1/client-work/${encodeURIComponent(id)}`)
        if (!res.ok) {
          throw new Error(`Failed to load case study: ${res.status} ${res.statusText}`)
        }
        const json = await res.json()
        if (json.success && json.data) {
          if (isMounted) {
            setCaseStudy(json.data)
          }
        } else {
          throw new Error(json.error || 'Unexpected response shape')
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch case study details')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchCaseStudy()
    return () => {
      isMounted = false
    }
  }, [id])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="text-center p-8">
          <p className="text-sm font-mono uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Loading Case Study...</p>
        </div>
      </div>
    )
  }

  if (error || !caseStudy) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="text-center max-w-md mx-auto p-8 rounded-card border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <h1 className="font-display text-2xl font-bold mb-4" style={{ color: 'var(--text)' }}>
            Case Study Not Found
          </h1>
          <p className="text-sm mb-8 leading-relaxed" style={{ color: 'var(--muted)' }}>
            {error || "We couldn't retrieve the detailed case study details."}
          </p>
          <Link to="/" className="btn-pill btn-primary no-underline inline-block">
            ← Back to Home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -15 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen pt-32 pb-24"
      style={{ background: 'var(--bg)', color: 'var(--text)' }}
    >
      <Helmet>
        <title>{`Case Study: ${caseStudy.client} | Ionic Errrrs Code`}</title>
        <meta name="description" content={`Detailed case study and outcomes for our partnership with ${caseStudy.client}.`} />
      </Helmet>

      <div className="container-page max-w-4xl px-4">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Link to="/" className="text-xs font-semibold uppercase tracking-wider no-underline hover:underline" style={{ color: 'var(--muted)' }}>
            ← Back to Client Work
          </Link>
        </div>

        {/* Header Hero Banner */}
        <header className="mb-12">
          <div className="flex flex-wrap gap-2.5 mb-4">
            <span className="badge">Case Study</span>
            <span className="badge badge-success" style={{ background: 'rgba(34, 197, 94, 0.08)', color: 'var(--accent-green)', borderColor: 'rgba(34, 197, 94, 0.2)' }}>
              {caseStudy.resultValue} {caseStudy.resultLabel}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-4" style={{ color: 'var(--text)' }}>
            {caseStudy.client}
          </h1>
          <p className="text-[1.125rem] leading-relaxed" style={{ color: 'var(--muted)' }}>
            {caseStudy.description}
          </p>
        </header>

        {/* Main Content Sections */}
        <div className="space-y-12">
          
          {/* Section 1: Linked Review (Client Review) */}
          {caseStudy.review && (
            <section>
              <h2 className="font-display text-lg font-bold uppercase tracking-wider mb-5" style={{ color: 'var(--text)' }}>
                Client's Review
              </h2>
              <div 
                className="card-studio relative overflow-hidden p-8"
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '16px'
                }}
              >
                <span className="absolute top-2 right-4 text-7xl select-none" style={{ color: 'rgba(255,255,255,0.03)', fontFamily: 'serif' }}>“</span>
                <div className="flex mb-4" style={{ color: '#fbbf24', gap: '3px', fontSize: '1.1rem' }}>
                  {'★'.repeat(caseStudy.review.rating)}
                </div>
                <blockquote className="text-[1.05rem] italic leading-relaxed mb-6" style={{ color: 'var(--text)', position: 'relative', zIndex: 1 }}>
                  "{caseStudy.review.quote}"
                </blockquote>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold font-display" 
                    style={{ background: 'var(--surface-2)', color: 'var(--muted)' }}
                  >
                    {caseStudy.review.authorInitials}
                  </div>
                  <div>
                    <div className="font-display text-sm font-bold" style={{ color: 'var(--text)' }}>
                      {caseStudy.review.authorName}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--muted)' }}>
                      {caseStudy.review.authorRole}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Section 2: Linked Project (Company Digital Product) */}
          {caseStudy.project ? (
            <section>
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-lg font-bold uppercase tracking-wider" style={{ color: 'var(--text)' }}>
                  Dynamic Company Project
                </h2>
                <span className="badge">Client Project</span>
              </div>
              <div 
                className="card-studio p-8"
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '16px'
                }}
              >
                <h3 className="font-display text-2xl font-bold mb-3" style={{ color: 'var(--text)' }}>
                  {caseStudy.project.title}
                </h3>
                <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--muted)' }}>
                  {caseStudy.project.description}
                </p>

                {/* Grid details */}
                <div className="grid grid-cols-2 gap-4 mb-6 text-xs border-t border-b py-4" style={{ borderColor: 'var(--border)' }}>
                  {caseStudy.project.duration && (
                    <div>
                      <span className="text-muted block uppercase tracking-wider mb-1">Development Time</span>
                      <strong style={{ color: 'var(--text)' }}>{caseStudy.project.duration}</strong>
                    </div>
                  )}
                  {caseStudy.project.team && (
                    <div>
                      <span className="text-muted block uppercase tracking-wider mb-1">Resources Allocated</span>
                      <strong style={{ color: 'var(--text)' }}>{caseStudy.project.team}</strong>
                    </div>
                  )}
                  {caseStudy.project.category && (
                    <div>
                      <span className="text-muted block uppercase tracking-wider mb-1">Project Domain</span>
                      <strong style={{ color: 'var(--text)' }}>{caseStudy.project.category}</strong>
                    </div>
                  )}
                  {caseStudy.project.status && (
                    <div>
                      <span className="text-muted block uppercase tracking-wider mb-1">Current Status</span>
                      <strong style={{ color: 'var(--text)' }}>{caseStudy.project.status}</strong>
                    </div>
                  )}
                </div>

                {/* Tech chips */}
                {caseStudy.project.technologies && caseStudy.project.technologies.length > 0 && (
                  <div className="mb-6">
                    <span className="text-xs text-muted block uppercase tracking-wider mb-2">Technologies Deployed</span>
                    <div className="flex flex-wrap gap-1.5">
                      {caseStudy.project.technologies.map(t => (
                        <span key={t} className="badge text-[0.7rem] py-1 px-2.5" style={{ background: 'var(--surface-2)' }}>{t}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Project Brief CTA */}
                <div className="pt-2">
                  <Link 
                    to={`/projects/${caseStudy.project.id}`} 
                    className="btn-pill btn-primary text-xs no-underline inline-flex items-center gap-2"
                  >
                    View Project Case Study & Gallery →
                  </Link>
                </div>
              </div>
            </section>
          ) : (
            <section className="text-center p-8 rounded-card border" style={{ borderColor: 'var(--border)', background: 'var(--surface-2)' }}>
              <p className="text-sm text-muted mb-0">This case study is not currently linked to a project details portfolio.</p>
            </section>
          )}
        </div>
      </div>
    </motion.div>
  )
}
